var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { brandConfiguration } from "@/brands/config";
import { CdpProvider } from "@/contexts/CdpProvider";
import { useFetcher } from "@aviv-unified-frontend/runtime/fetcher";
import { useLocale } from "@aviv-unified-frontend/runtime/i18n";
import { createApplication, useServerRequest, } from "@aviv-unified-frontend/runtime/lifecycle";
import { createLogger } from "@aviv-unified-frontend/runtime/monitoring";
import { useLocation } from "@aviv-unified-frontend/runtime/router";
import { CdpRoot, Head, Loading } from "@/components";
import { HttpCode, IS_SERVER } from "@/constants";
import { useCdpConfig, useDummyFetcher } from "@/hooks";
import { useLoadTranslations } from "@/shared/hooks/useI18n/useLoadTranslations";
import { buildRedirectUrl, getBffurl, getFetcher, getPreviewMode, } from "@/utils";
import { getUpdatedFeatureFlags } from "@/utils/getUpdatedFeatureFlags";
import pkg from "../../package.json";
import { getClassifiedIdFromURL } from "@/utils/getClassifiedIdFromURL";
const env = process.env.CONFIG_ENV;
const { brand, locale, kameleoon } = brandConfiguration;
export const App = () => {
    var _a;
    const { pathname, search } = useLocation();
    const { language } = useLocale();
    useLoadTranslations();
    // SSR data is fetched by the Shell using the onServerRequest lifecycle (see https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/tree/main/libraries/runtime/src/lifecycle#useserverrequest)
    // Usually all the data is available in SSR
    const ssrData = (_a = useServerRequest()) === null || _a === void 0 ? void 0 : _a.data;
    // This useFetcher is a fallbacks for when SSR data is not available
    // So, by default, we bypass the useFetcher by providing a 'dummy' fetcher which resolves to a null value
    const dummyFetcher = useDummyFetcher();
    const hasForceUpdate = useRef(false);
    // We keep the currently active fetcher in a state. We swap it only once on CSR bootstrap.
    const [activeFetcher, setActiveFetcher] = useState(dummyFetcher);
    const [fetchKey, setFetchKey] = useState("cldp:noop"); // route-scoped key will be set on CSR bootstrap
    // By default the useFetcher will get a dummy fetcher and it won't do any actual BFF data-fetching
    const { data: csrData, error: csrError, isLoading, forceUpdate, flush, } = useFetcher(IS_SERVER ? "cldp:ssr-disabled" : fetchKey, // prevent touching real key on SSR
    IS_SERVER ? () => __awaiter(void 0, void 0, void 0, function* () { return null; }) : activeFetcher, // no-op fetcher on SSR (never rejects)
    { prefetching: false } // disable server-side prefetch
    );
    useEffect(() => {
        if (IS_SERVER)
            return;
        if ((ssrData === null || ssrData === void 0 ? void 0 : ssrData.classified) || (csrData === null || csrData === void 0 ? void 0 : csrData.classified) || csrError)
            return;
        if (hasForceUpdate.current)
            return;
        const searchParams = new URLSearchParams(search);
        const { classifiedId } = getClassifiedIdFromURL(pathname);
        const previewMode = getPreviewMode(pathname);
        let bffUrl = getBffurl({
            env,
            classifiedId,
            brand,
            search: searchParams,
            previewMode,
            isCsr: true,
        });
        if (process.env.SANDBOX_SSR) {
            bffUrl = `http://${process.env.SANDBOX_SSR}/expose/${classifiedId}`;
        }
        // The dummy fetch may have cached null under the previous key, flush it
        flush(fetchKey);
        // Swap to real fetcher + a route-scoped key, and mark that weve kicked CSR once
        const newKey = `cldp:${classifiedId}:${language}:${searchParams.toString()}`;
        setActiveFetcher(() => getFetcher(bffUrl, language));
        setFetchKey(newKey);
        hasForceUpdate.current = true;
        void forceUpdate({ updateCache: true });
    }, [
        ssrData === null || ssrData === void 0 ? void 0 : ssrData.classified,
        csrData === null || csrData === void 0 ? void 0 : csrData.classified,
        csrError,
        pathname,
        search,
        language,
        forceUpdate,
        flush,
        fetchKey,
    ]);
    const classified = useMemo(() => {
        const classified = (ssrData === null || ssrData === void 0 ? void 0 : ssrData.classified) || (csrData === null || csrData === void 0 ? void 0 : csrData.classified) || null;
        if (!classified)
            return null;
        const updated = getUpdatedFeatureFlags(classified.featureFlags, classified.featureFlagsTracking);
        return Object.assign(Object.assign({}, classified), { featureFlags: updated.featureFlags, featureFlagsTracking: updated.featureFlagsTracking });
    }, [ssrData === null || ssrData === void 0 ? void 0 : ssrData.classified, csrData === null || csrData === void 0 ? void 0 : csrData.classified]);
    const error = (ssrData === null || ssrData === void 0 ? void 0 : ssrData.error) || (csrData === null || csrData === void 0 ? void 0 : csrData.error) || null;
    // @ts-ignore
    const config = useCdpConfig(env, classified);
    const pageProps = {
        config,
        classified,
        error,
    };
    if (process.env.CONFIG_ENV === "dev") {
        console.log(pageProps);
        console.log({ ssrData, csrData, isLoading, csrError });
    }
    // Before we even kicked CSR
    if (!ssrData && fetchKey === "cldp:noop" && !hasForceUpdate.current) {
        return _jsx(Loading, {});
    }
    // After CSR was kicked: while the real fetch is in-flight, or still no CSR payload nor error
    if (!ssrData &&
        hasForceUpdate.current &&
        (isLoading || (!csrData && !csrError))) {
        return _jsx(Loading, {});
    }
    return (
    // @ts-ignore
    _jsxs(CdpProvider, Object.assign({}, pageProps, { children: [_jsx(Head, {}), _jsx(CdpRoot, Object.assign({}, pageProps))] })));
};
// @todo: upload sourceMap https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/tree/main/libraries/runtime/src/monitoring#upload-source-maps
const logger = createLogger("app_cldp");
// eslint-disable-next-line import/no-default-export
export default createApplication({
    Component: App,
    id: "app_cldp",
    onServerRequest(request, response) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const { classifiedId } = getClassifiedIdFromURL(request.url.pathname);
            const env = process.env.CONFIG_ENV;
            // kameleoon
            let kameleoonFlags = null;
            if (kameleoon) {
                const { getServerKameleoonFlags } = yield import("../utils/getServerKameleoonFlags");
                // @ts-ignore
                kameleoonFlags = yield getServerKameleoonFlags(request, kameleoon.siteCode, kameleoon.env);
            }
            else {
                console.info("[Kameleoon] Kameleoon is not configured for this brand, skipping flags retrieval.");
            }
            const kameleoonData = {};
            if (kameleoonFlags != null) {
                // @ts-ignore
                kameleoonData.featureFlags = Object.assign({}, kameleoonFlags.featureFlags);
                kameleoonData.featureFlagsTracking = Object.assign({}, kameleoonFlags.featureFlagsTracking);
            }
            const previewMode = getPreviewMode(request.url.pathname);
            const bffUrl = getBffurl({
                env,
                classifiedId,
                brand,
                search: request.url.searchParams,
                previewMode,
            });
            const xForwardedFor = request === null || request === void 0 ? void 0 : request.getHeader("X-Forwarded-For");
            // @ts-ignore
            const fetcher = getFetcher(bffUrl, locale);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore conflic between NextJS and Shell on the xForwardedFor Header
            const { classified = null, error = null } = yield fetcher(xForwardedFor);
            const seoUrl = (_b = (_a = classified === null || classified === void 0 ? void 0 : classified.seo) === null || _a === void 0 ? void 0 : _a.headInfo) === null || _b === void 0 ? void 0 : _b.redirectUrl;
            const redirectUrl = buildRedirectUrl(request.url, seoUrl);
            if (!previewMode && redirectUrl) {
                response.redirect(redirectUrl, 301);
                return {
                    data: {
                        classified,
                        error,
                        kameleoonData,
                    },
                };
            }
            if (error === null || error === void 0 ? void 0 : error.code) {
                if (error.code === HttpCode.GONE) {
                    response.notFound(410);
                }
                else {
                    response.notFound();
                }
            }
            if (classified && kameleoonFlags != null) {
                // @ts-ignore
                classified.featureFlags = kameleoonFlags.featureFlags;
                // @ts-ignore
                classified.featureFlagsTracking = kameleoonFlags.featureFlagsTracking;
            }
            return {
                data: {
                    classified,
                    error,
                    kameleoonData,
                },
            };
        });
    },
    logger,
    version: pkg.version,
});
